import { DatePipe, DecimalPipe, UpperCasePipe } from "@angular/common";
import { Component, Inject, OnInit, signal } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { ImageUrlDirective } from "@app/directives";
import {
  type CustomerCartView,
  CustomerCartViewComponent,
} from "@components/customer-cart-view/customer-cart-view.component";
import { RequestService } from "@services/request/request.service";
import { forkJoin } from "rxjs";

export interface UserCartDialogData {
  userId: number;
  userName: string;
}

interface UserProfile {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  phone_country_code: string;
  address: string;
  postal_code: string;
  profile_pic: string;
  created_on: string;
  iso2: string;
  browsing_iso2: string;
  status: string;
  quiz_result: string | { fishType?: string; tankCapacity?: string };
  address_email: string;
}

@Component({
  selector: "fh-user-cart-dialog",
  standalone: true,
  imports: [
    DatePipe,
    DecimalPipe,
    UpperCasePipe,
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    ImageUrlDirective,
    CustomerCartViewComponent,
  ],
  styles: [`
    mat-dialog-content {
      min-width: 700px;
      max-width: 960px;
      padding: 0 24px 8px;
    }

    /* ── Header ── */
    .dialog-title {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 20px 24px 12px;
      border-bottom: 1px solid #f0f0f0;
    }
    .dialog-title mat-icon { color: #1976d2; }
    .dialog-title h2 { margin: 0; font-size: 18px; font-weight: 600; }
    .dialog-title .sub { font-size: 14px; color: #666; margin-left: 2px; }

    /* ── Loading ── */
    .loading-wrap {
      display: flex; flex-direction: column; align-items: center;
      justify-content: center; padding: 56px 0; gap: 14px; color: #888;
    }

    /* ── Profile card ── */
    .profile-card {
      display: flex;
      gap: 20px;
      padding: 20px 0 16px;
      border-bottom: 2px solid #f0f0f0;
    }
    .avatar-wrap {
      flex-shrink: 0;
      width: 72px; height: 72px;
      border-radius: 50%;
      overflow: hidden;
      border: 2px solid #e0e0e0;
      background: #e8f0fe;
      display: flex; align-items: center; justify-content: center;
    }
    .avatar-wrap img { width: 100%; height: 100%; object-fit: cover; }
    .avatar-initials {
      font-size: 24px; font-weight: 700; color: #1976d2;
      text-transform: uppercase; line-height: 1;
    }
    .profile-info { flex: 1; min-width: 0; }
    .profile-name {
      font-size: 16px; font-weight: 600; color: #1a1a1a; margin: 0 0 4px;
    }
    .status-badge {
      display: inline-block;
      padding: 2px 8px; border-radius: 99px;
      font-size: 11px; font-weight: 600; text-transform: uppercase;
      margin-bottom: 10px;
    }
    .status-badge.active   { background: #dcfce7; color: #16a34a; }
    .status-badge.inactive { background: #fee2e2; color: #dc2626; }
    .status-badge.other    { background: #f1f5f9; color: #64748b; }
    .profile-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 6px 24px;
    }
    .profile-field { display: flex; align-items: flex-start; gap: 6px; min-width: 0; }
    .profile-field mat-icon { font-size: 15px; width: 15px; height: 15px; color: #888; margin-top: 1px; flex-shrink: 0; }
    .pf-content { min-width: 0; }
    .pf-label { font-size: 11px; color: #999; line-height: 1.2; }
    .pf-value { font-size: 13px; color: #333; word-break: break-word; }
    .pf-value.empty { color: #bbb; }

    /* ── Section title ── */
    .section-title {
      font-size: 13px; font-weight: 600; color: #555;
      text-transform: uppercase; letter-spacing: 0.5px;
      margin: 18px 0 10px;
    }

    /* ── Cart table ── */
    .cart-table-wrap { overflow-x: auto; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    thead th {
      background: #f5f7fa; padding: 9px 10px;
      text-align: left; font-weight: 600; color: #444;
      border-bottom: 2px solid #e0e0e0; white-space: nowrap;
    }
    tbody td {
      padding: 9px 10px; border-bottom: 1px solid #f0f0f0;
      vertical-align: middle; color: #333;
    }
    tbody tr:last-child td { border-bottom: none; }
    tbody tr:hover td { background: #fafafa; }
    .prod-img {
      width: 44px; height: 44px; object-fit: cover;
      border-radius: 6px; border: 1px solid #eee;
    }
    .prod-name { font-weight: 500; }
    .opt-name  { color: #888; font-size: 12px; }

    /* ── Empty cart ── */
    .empty-cart {
      text-align: center; padding: 32px 0;
      color: #aaa; font-size: 14px;
    }
    .empty-cart mat-icon {
      display: block; margin: 0 auto 8px;
      font-size: 36px; width: 36px; height: 36px; color: #ddd;
    }

    /* ── Price summary ── */
    .price-summary {
      display: flex; justify-content: flex-end; gap: 28px;
      margin-top: 12px; padding: 12px 14px;
      background: #f5f7fa; border-radius: 8px;
    }
    .pr { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; }
    .pr .lbl { font-size: 11px; color: #888; }
    .pr .amt { font-size: 14px; font-weight: 600; color: #333; }
    .pr.total .amt { font-size: 16px; color: #1976d2; }

    mat-dialog-actions { justify-content: flex-end; padding: 8px 24px 16px; }
  `],
  template: `
    <div class="dialog-title">
      <mat-icon>person</mat-icon>
      <h2>User Overview</h2>
      <span class="sub">· {{ _data.userName }}</span>
    </div>

    <mat-dialog-content>
      @if (isLoading()) {
        <div class="loading-wrap">
          <mat-spinner diameter="36" />
          <span>Loading…</span>
        </div>
      } @else {

        <!-- ── Profile ── -->
        @if (profile()) {
          <div class="profile-card">
            <div class="avatar-wrap">
              @if (profile()!.profile_pic) {
                <img fhImageUrl [originalUrl]="profile()!.profile_pic" alt="avatar" />
              } @else {
                <span class="avatar-initials">{{ initials() }}</span>
              }
            </div>

            <div class="profile-info">
              <p class="profile-name">{{ profile()!.first_name }} {{ profile()!.last_name }}</p>
              <span class="status-badge" [class]="'status-badge ' + (profile()!.status || 'other')">
                {{ profile()!.status || 'unknown' }}
              </span>

              <div class="profile-grid">
                <div class="profile-field">
                  <mat-icon>email</mat-icon>
                  <div class="pf-content">
                    <div class="pf-label">Email</div>
                    <div class="pf-value" [class.empty]="!profile()!.email">
                      {{ profile()!.email || '—' }}
                    </div>
                  </div>
                </div>

                <div class="profile-field">
                  <mat-icon>phone</mat-icon>
                  <div class="pf-content">
                    <div class="pf-label">Phone</div>
                    <div class="pf-value" [class.empty]="!profile()!.phone_number">
                      {{ profile()!.phone_number
                          ? (profile()!.phone_country_code || '') + profile()!.phone_number
                          : '—' }}
                    </div>
                  </div>
                </div>

                <div class="profile-field">
                  <mat-icon>home</mat-icon>
                  <div class="pf-content">
                    <div class="pf-label">Address</div>
                    <div class="pf-value" [class.empty]="!profile()!.address">
                      {{ profile()!.address || '—' }}
                    </div>
                  </div>
                </div>

                <div class="profile-field">
                  <mat-icon>markunread_mailbox</mat-icon>
                  <div class="pf-content">
                    <div class="pf-label">Postal Code</div>
                    <div class="pf-value" [class.empty]="!profile()!.postal_code">
                      {{ profile()!.postal_code || '—' }}
                    </div>
                  </div>
                </div>

                <div class="profile-field">
                  <mat-icon>public</mat-icon>
                  <div class="pf-content">
                    <div class="pf-label">Country</div>
                    <div class="pf-value" [class.empty]="!profile()!.browsing_iso2">
                      {{ profile()!.browsing_iso2 ? (profile()!.browsing_iso2 | uppercase) : '—' }}
                    </div>
                  </div>
                </div>

                <div class="profile-field">
                  <mat-icon>calendar_today</mat-icon>
                  <div class="pf-content">
                    <div class="pf-label">Joined</div>
                    <div class="pf-value" [class.empty]="!profile()!.created_on">
                      {{ profile()!.created_on ? (profile()!.created_on | date: 'dd MMM yyyy') : '—' }}
                    </div>
                  </div>
                </div>

                @if (fishType() || tankCapacity()) {
                  <div class="profile-field">
                    <mat-icon>set_meal</mat-icon>
                    <div class="pf-content">
                      <div class="pf-label">Fish Type</div>
                      <div class="pf-value" [class.empty]="!fishType()">
                        {{ fishType() || '—' }}
                      </div>
                    </div>
                  </div>

                  <div class="profile-field">
                    <mat-icon>water</mat-icon>
                    <div class="pf-content">
                      <div class="pf-label">Tank Capacity</div>
                      <div class="pf-value" [class.empty]="!tankCapacity()">
                        {{ tankCapacity() || '—' }}
                      </div>
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>
        }

        <!-- ── Cart ── -->
        <div class="section-title">
          <mat-icon style="font-size:14px;vertical-align:middle;margin-right:4px;">shopping_cart</mat-icon>
          Cart
        </div>

        @if (!cart()) {
          <div class="empty-cart">
            <mat-icon>remove_shopping_cart</mat-icon>
            No items in cart.
          </div>
        } @else {
          <fh-customer-cart-view [cart]="cart()!"></fh-customer-cart-view>
        }
      }
    </mat-dialog-content>

    <mat-dialog-actions>
      <button mat-button (click)="close()">Close</button>
    </mat-dialog-actions>
  `,
})
export class UserCartDialogComponent implements OnInit {
  isLoading = signal(true);
  profile = signal<UserProfile | null>(null);
  /** The cart as the customer sees it, or null when they have none. */
  cart = signal<CustomerCartView | null>(null);

  initials = (): string => {
    const p = this.profile();
    if (!p) return "?";
    const f = (p.first_name ?? "").trim()[0] ?? "";
    const l = (p.last_name ?? "").trim()[0] ?? "";
    return (f + l) || (p.email?.[0] ?? "?");
  };

  fishType = (): string => {
    const p = this.profile();
    if (!p?.quiz_result) return "";
    try {
      const q = typeof p.quiz_result === "string" ? JSON.parse(p.quiz_result) : p.quiz_result;
      return q?.fishType ?? "";
    } catch { return ""; }
  };

  tankCapacity = (): string => {
    const p = this.profile();
    if (!p?.quiz_result) return "";
    try {
      const q = typeof p.quiz_result === "string" ? JSON.parse(p.quiz_result) : p.quiz_result;
      return q?.tankCapacity ?? "";
    } catch { return ""; }
  };

  constructor(
    private readonly requestService: RequestService,
    public readonly dialogRef: MatDialogRef<UserCartDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly _data: UserCartDialogData
  ) {}

  ngOnInit(): void {
    forkJoin({
      userRes: this.requestService.get<any>(`/api/v1/user_details?id=${this._data.userId}`),
      cartRes: this.requestService.get<any>(`/api/v1/get-user-cart?id=${this._data.userId}`),
    }).subscribe({
      next: ({ userRes, cartRes }) => {
        if (userRes?.result_code === 1) {
          this.profile.set(userRes.data);
        }
        if (cartRes?.result_code === 1) {
          this.cart.set(cartRes.data?.cart ?? null);
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
